import { useEffect, useState } from 'react';
import pushNotificationService from '../service/pushNotification.service';

/**
 * Hook React pour gérer l'initialisation des push notifications
 * Usage: Appeler dans App.tsx au montage du composant
 */
export const usePushNotifications = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    const supported = pushNotificationService.isSupported();
    setIsSupported(supported);

    if (!supported) {
      console.log('📱 Push notifications non disponibles sur cette plateforme');
      return;
    }

    if (!isInitialized) {
      console.log('🚀 Préparation initialisation push (plateforme native détectée)');
      initializePushNotifications();
    }
  }, [isInitialized]);

  useEffect(() => {
    if (!isSupported) {
      console.log('⏸️ Listeners auth non activés: push non supporté');
      return;
    }

    const handleAuthLogin = () => {
      console.log('🔐 Connexion détectée, envoi éventuel du token en attente...');
      pushNotificationService
        .sendPendingToken()
        .then(() => console.log('📡 Token en attente traité (si présent)'))
        .catch((err) => console.error('❌ Erreur envoi token en attente:', err));
    };

    console.log('👂 Ajout listener momentum:auth-login');
    window.addEventListener('momentum:auth-login', handleAuthLogin);

    return () => {
      console.log('🧹 Suppression listener momentum:auth-login');
      window.removeEventListener('momentum:auth-login', handleAuthLogin);
    };
  }, [isSupported]);

  const initializePushNotifications = async () => {
    try {
      console.log('⚙️ Initialisation des push notifications (Capacitor)...');

      await pushNotificationService.initialize();

      // Envoyer un token en attente si disponible
      await pushNotificationService.sendPendingToken();

      setIsInitialized(true);
      setError(null);

      console.log('✅ Push notifications initialisées avec succès');
    } catch (err) {
      console.error('❌ Erreur initialisation push:', err);
      setError(err as Error);

      // Ne pas bloquer l'app si les push ne fonctionnent pas
      // L'utilisateur peut continuer à utiliser l'app normalement
    }
  };

  /**
   * Réinitialiser les push notifications (après connexion par exemple)
   */
  const reinitialize = async () => {
    setIsInitialized(false);
    setError(null);
    await initializePushNotifications();
  };

  /**
   * Nettoyer les push notifications (avant déconnexion)
   */
  const cleanup = async () => {
    try {
      await pushNotificationService.removeToken();
      await pushNotificationService.removeAllDeliveredNotifications();
      setIsInitialized(false);
      console.log(' Push notifications nettoyées');
    } catch (err) {
      console.error('Erreur nettoyage push:', err);
    }
  };

  return {
    isInitialized,
    isSupported,
    error,
    retry: initializePushNotifications,
    reinitialize,
    cleanup,
  };
};

export default usePushNotifications;

